import { Context, Effect, Layer } from 'effect'

export type ClipboardError =
  | { _tag: 'ClipboardError.Unavailable'; message: string }
  | { _tag: 'ClipboardError.WriteFailed'; message: string; cause: unknown }

type ClipboardService = {
  copy: (text: string) => Effect.Effect<void, ClipboardError>
}

export class Clipboard extends Context.Service<Clipboard, ClipboardService>()('Clipboard') {}

export function clipboardLayer() {
  return Layer.succeed(Clipboard, {
    copy: (text) =>
      Effect.suspend((): Effect.Effect<void, ClipboardError> => {
        const writeText = globalThis.navigator?.clipboard?.writeText
        if (!writeText) {
          return Effect.fail({
            _tag: 'ClipboardError.Unavailable',
            message: 'Clipboard API unavailable'
          })
        }

        return Effect.tryPromise({
          try: () => writeText.call(globalThis.navigator.clipboard, text),
          catch: (cause) => ({
            _tag: 'ClipboardError.WriteFailed' as const,
            message: 'Failed to copy to clipboard',
            cause
          })
        }).pipe(
          Effect.timeoutOrElse({
            duration: '2 seconds',
            orElse: () =>
              Effect.fail({
                _tag: 'ClipboardError.WriteFailed' as const,
                message: 'Timed out copying to clipboard',
                cause: new Error('Clipboard write did not settle within 2 seconds')
              })
          })
        )
      })
  })
}

export function clipboardNoopLayer() {
  return Layer.succeed(Clipboard, {
    copy: () => Effect.void
  })
}
